// Loading placeholders — four shapes, not one.
//
// The old page had a single eight-cell `SkeletonGrid`, which is wrong for a page
// made of rails, walls, rows and blocks: a rail skeleton that renders as a grid
// makes the layout jump when the real content arrives. Each shape mirrors the
// geometry of the thing it stands in for, so nothing reflows.
//
// All of these are `aria-hidden`; the announcement comes from the wrapper in
// `AsyncSection`.

export function RailSkeleton({ count = 7 }: { count?: number }) {
  return (
    <div
      className="mt-5 flex gap-4 overflow-hidden sm:grid sm:grid-cols-4 sm:gap-6 lg:grid-cols-7"
      aria-hidden="true"
    >
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="w-[38%] shrink-0 sm:w-auto">
          <div className="skeleton aspect-[2/3] w-full rounded-[7px]" />
          <div className="skeleton mt-2 h-4 w-11/12 rounded-sm" />
          <div className="skeleton mt-1.5 h-3 w-2/3 rounded-sm" />
        </div>
      ))}
    </div>
  );
}

export function WallSkeleton({ count = 18 }: { count?: number }) {
  return (
    <div
      className="mt-5 grid grid-cols-3 gap-4 sm:grid-cols-4 sm:gap-5 lg:grid-cols-6"
      aria-hidden="true"
    >
      {Array.from({ length: count }).map((_, i) => (
        <div key={i}>
          <div className="skeleton aspect-[2/3] w-full rounded-[6px]" />
          <div className="skeleton mt-2 h-3.5 w-11/12 rounded-sm" />
          <div className="skeleton mt-1.5 h-2.5 w-1/2 rounded-sm" />
        </div>
      ))}
    </div>
  );
}

export function RowSkeleton({ count = 3 }: { count?: number }) {
  return (
    <div className="mt-5 divide-y divide-hairline" aria-hidden="true">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="flex items-center gap-4 py-4">
          <div className="skeleton h-[68px] w-[46px] shrink-0 rounded-[4px]" />
          <div className="min-w-0 flex-1">
            <div className="skeleton h-4 w-2/5 rounded-sm" />
            <div className="skeleton mt-2 h-2.5 w-1/4 rounded-sm" />
            <div className="skeleton mt-3 h-[5px] w-full rounded-sm" />
          </div>
        </div>
      ))}
    </div>
  );
}

/** A hero, promo or collection tile — sized so the page doesn't jump. */
export function BlockSkeleton({ className = "h-[340px]" }: { className?: string }) {
  return <div className={`skeleton w-full rounded-2xl ${className}`} aria-hidden="true" />;
}

export function ChipGridSkeleton({ count = 20 }: { count?: number }) {
  return (
    <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5 lg:gap-3.5" aria-hidden="true">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="skeleton h-[62px] rounded-xl" />
      ))}
    </div>
  );
}
